import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CardPayment } from '@lib/entities';
import { CardOrOneTimePayment, GiftCard, isSavedCardPayment } from '@lib/entities/Payment';
import { AddPaymentV4Payload } from '@lib/api/CheckoutService';
import { type ApiError } from '@lib/api/client';
import { loggedOut } from '@lib/redux/actions/lifecycle';
import { type DeferredAction, withDeferred } from '@lib/redux/deferred';

export interface WalletState {
    loading: boolean;
    /**
     * True once at least one wallet fetch has SETTLED this session. Distinguishes "not
     * fetched yet" from "fetched and empty": the payment-methods page defers its first
     * fetch until the MFA flag resolves, and `loading` is false during that window — so
     * an empty-state gated on `!loading && !cards.length` alone flashes "No Payments"
     * at users who do have cards (the JSAD-6919 loading-vs-empty class, wallet edition;
     * it also made the nightly account-settings spec misreport a healthy wallet as a
     * "vault miss" every night from 2026-08-26).
     *
     * Set by the writes walletSaga makes when a fetch settles (`setCards` on success,
     * `setWalletError` on failure) — the Zustand `fetchWallet:finishLoading` (Wallet.ts:163).
     * Refetches never reset it, so the empty state cannot re-flash on background refreshes.
     */
    hasFetched: boolean;
    cards: CardPayment[];
    selectedCard?: CardOrOneTimePayment;
    giftCards?: GiftCard[];
    jsCreditBalance: number;
    error: string | null;
    gatewayLegend?: string;
    /** True when wallet-info returns a 428 requiring device verification. */
    requireDeviceVerification?: boolean;
    /**
     * True (from wallet-info, v4+) when the user has a saved card stranded on the
     * inactive gateway with no equivalent on the active one. Drives the differentiated
     * "re-add your card" empty state (JSAD-6863).
     */
    savedCardNeedsReentry?: boolean;
    /**
     * How the user paid on their most recent order, per wallet-info (v4+, JSAD-7344).
     *
     * Fast Reorder REQUIRES this: it charges without a payment step, so it may only use a
     * method the user demonstrably chose before. `'none'`, absent, or a `'saved_card'`
     * whose card (`lastUsedCardId`) is no longer in the wallet means there is nothing it is
     * entitled to charge, and the pre-flight fails rather than silently falling back to
     * `cards[0]` (useFastReorder.ts `resolveLastUsedPayment`).
     *
     * Regular checkout is unaffected: it still pre-selects a card, because the user
     * confirms it on screen.
     */
    lastUsedPaymentType?: 'saved_card' | 'apple_pay' | 'none';
    /**
     * Id of wallet-info's `last_payment_method_used`. Kept apart from `selectedCard`, which
     * falls back to `cards[0]` and can hold a one-time card from an earlier checkout, so
     * Fast Reorder can tell the card the user last paid with from whatever is selected.
     */
    lastUsedCardId?: CardPayment['id'];
}

/**
 * Request payloads. The four request actions carry an optional deferred in `meta`
 * (lib/redux/deferred.ts) so the `useWallet.redux` twin can hand its callers the Promise the
 * Zustand wallet returned (`addCard(): Promise<ApiError | undefined>`, the others
 * `Promise<void>`). Dispatchers that pass only the payload are unchanged.
 */
export interface FetchWalletPayload {
    userId?: number;
    /** When true, the first card in the results becomes the `selectedCard`. */
    selectFirstCard?: boolean;
    storeId?: number;
    /** When true, hit the v5 wallet-info endpoint instead of v4 (MFA-aware). */
    isMfaEnabled?: boolean;
}

export interface AddCardPayload {
    newCard: AddPaymentV4Payload;
    cc_last4: string;
    userId: number;
    storeId?: number;
}

export interface RemoveCardPayload {
    card: CardPayment;
    userId: number;
    storeId?: number;
}

export interface RemoveSelectedCardPayload {
    userId: number;
    storeId?: number;
}

export type FetchWalletAction = DeferredAction<FetchWalletPayload, void>;
export type AddCardAction = DeferredAction<AddCardPayload, ApiError | undefined>;
export type RemoveCardAction = DeferredAction<RemoveCardPayload, void>;
export type RemoveSelectedCardAction = DeferredAction<RemoveSelectedCardPayload, void>;

const initialState: WalletState = {
    loading: false,
    hasFetched: false,
    cards: [],
    selectedCard: undefined,
    jsCreditBalance: 0,
    error: null,
    requireDeviceVerification: false,
    savedCardNeedsReentry: false
};

/**
 * True when wallet-info said the last order was Apple Pay and `selectedCard` is still the
 * fetch's stand-in — `cards[0]`, which the user never chose (the saga cannot select Apple Pay:
 * its usability needs the quote). Checkout's Apple Pay auto-select honors this ONCE per
 * mount, which is what replaced the device-local persisted selection (JSAD-7344): the backend
 * answer now follows the user to every device. A one-time card, or any card that is not
 * `cards[0]`, is a choice the user made and is left alone.
 */
export const hasUnhonoredLastUsedApplePay = (
    wallet: Pick<WalletState, 'lastUsedPaymentType' | 'selectedCard' | 'cards'>
): boolean =>
    wallet.lastUsedPaymentType === 'apple_pay' &&
    isSavedCardPayment(wallet.selectedCard) &&
    wallet.selectedCard.id === wallet.cards[0]?.id;

const walletSlice = createSlice({
    name: 'wallet',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setCards: (state, action: PayloadAction<CardPayment[]>) => {
            state.cards = action.payload;
            state.hasFetched = true;
        },
        setSelectedCard: (state, action: PayloadAction<CardOrOneTimePayment | undefined>) => {
            state.selectedCard = action.payload;
        },
        setGiftCards: (state, action: PayloadAction<GiftCard[]>) => {
            state.giftCards = action.payload;
        },
        setJsCreditBalance: (state, action: PayloadAction<number>) => {
            state.jsCreditBalance = action.payload;
        },
        /** Reset the "wallet has answered" gate so a consumer can await a fresh fetch. */
        setWalletFetched: (state, action: PayloadAction<boolean>) => {
            state.hasFetched = action.payload;
        },
        setWalletError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            // "we asked and it failed" still counts as a settled fetch (see `hasFetched`).
            if (action.payload !== null) {
                state.hasFetched = true;
            }
        },
        setGatewayLegend: (state, action: PayloadAction<string | undefined>) => {
            state.gatewayLegend = action.payload;
        },
        setRequireDeviceVerification: (state, action: PayloadAction<boolean>) => {
            state.requireDeviceVerification = action.payload;
        },
        setSavedCardNeedsReentry: (state, action: PayloadAction<boolean>) => {
            state.savedCardNeedsReentry = action.payload;
        },
        setLastUsedPaymentType: (
            state,
            action: PayloadAction<WalletState['lastUsedPaymentType']>
        ) => {
            state.lastUsedPaymentType = action.payload;
        },
        setLastUsedCardId: (state, action: PayloadAction<WalletState['lastUsedCardId']>) => {
            state.lastUsedCardId = action.payload;
        },
        clearSelectedCard: (state) => {
            state.selectedCard = undefined;
        },
        resetWallet: (state) => {
            return initialState;
        },
        // Actions to trigger sagas (deferred in `meta`, see the payload types above)
        fetchWalletRequest: {
            reducer: (state, _action: FetchWalletAction) => {
                state.loading = true;
                state.error = null;
            },
            prepare: withDeferred<FetchWalletPayload, void>()
        },
        addCardRequest: {
            reducer: (state, _action: AddCardAction) => {
                state.loading = true;
            },
            prepare: withDeferred<AddCardPayload, ApiError | undefined>()
        },
        removeCardRequest: {
            reducer: (state, _action: RemoveCardAction) => {
                state.loading = true;
            },
            prepare: withDeferred<RemoveCardPayload, void>()
        },
        removeSelectedCardRequest: {
            reducer: (state, _action: RemoveSelectedCardAction) => {
                state.loading = true;
            },
            prepare: withDeferred<RemoveSelectedCardPayload, void>()
        }
    },
    extraReducers: (builder) => {
        // `clearLoggedInState` → `resetWallet()` (lib/hooks/useAuth.ts:120).
        builder.addCase(loggedOut, () => initialState);
    }
});

export const {
    setLoading,
    setCards,
    setSelectedCard,
    setGiftCards,
    setJsCreditBalance,
    setWalletError,
    setWalletFetched,
    setGatewayLegend,
    setRequireDeviceVerification,
    setSavedCardNeedsReentry,
    setLastUsedPaymentType,
    setLastUsedCardId,
    clearSelectedCard,
    resetWallet,
    fetchWalletRequest,
    addCardRequest,
    removeCardRequest,
    removeSelectedCardRequest
} = walletSlice.actions;

export const selectWallet = (state: { wallet: WalletState }) => state.wallet;
export const selectSelectedCard = (state: { wallet: WalletState }) => state.wallet.selectedCard;
export const selectGatewayLegend = (state: { wallet: WalletState }) => state.wallet.gatewayLegend;
export const selectSavedCardNeedsReentry = (state: { wallet: WalletState }) =>
    state.wallet.savedCardNeedsReentry;

export default walletSlice.reducer;
